'use client';
/**
 * LA PAGE KPI — LE GRAPHE D'ABORD (B1).
 * ─────────────────────────────────────
 * Demande de la RQ : « atterrir sur un graphe avec une liste déroulante : je
 * change d'indicateur, le graphe change. Données collectées en direct,
 * granularité au choix — jour, mois, trimestre, année —, type de graphe au
 * choix. Un outil de suivi de l'activité, pas un tableau de chiffres. »
 *
 * ── Trois choses que cet écran refuse de faire ────────────────────────────
 *
 *  · **Recalculer quoi que ce soit.** Chaque point vient de
 *    `GET /ged/kpi/serie`, qui rappelle le calcul du dossier de revue de
 *    direction. Une seconde formule aurait fini par diverger, et les deux
 *    écrans auraient raconté deux histoires du même trimestre.
 *
 *  · **Relier deux points à travers un trou.** Une période non calculable
 *    n'est pas un zéro : la ligne s'interrompt, et le tableau dit pourquoi.
 *    Tracer une continuité qui n'a pas existé serait le plus sûr moyen de
 *    faire commenter une tendance imaginaire.
 *
 *  · **Présenter une période entamée comme une période révolue.** Le mois en
 *    cours est hachuré et annoncé — sans quoi on lit une chute là où il n'y a
 *    qu'un mois pas fini.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, Skeleton, RefISO } from '@/components/ui';
import { H1 } from './ressources/commun';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
type Granularite = 'jour' | 'mois' | 'trimestre' | 'annee';
export type Forme = 'courbe' | 'barres' | 'aires';

export interface Valeur {
  valeur: number | null; calculable: boolean; motif: string; texte: string;
  numerateur: number | null; denominateur: number | null;
}
export interface Point {
  cle: string; libelle: string; debut: string; fin: string; tronquee: boolean;
  valeurs: Record<string, Valeur>;
}
export interface Def { cle: string; libelle: string; unite: string | null; aide: string; sensSuggere: string }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ctx = any;
interface Serie {
  granularite: Granularite; du: string; au: string;
  catalogue: Def[]; cles: string[]; points: Point[];
  avertissement: string; rappel: string;
}

const GRANULARITES: { k: Granularite; label: string }[] = [
  { k: 'jour', label: 'Jour' }, { k: 'mois', label: 'Mois' },
  { k: 'trimestre', label: 'Trimestre' }, { k: 'annee', label: 'Année' },
];
const FORMES: { k: Forme; label: string; icone: string }[] = [
  { k: 'courbe', label: 'Courbe', icone: 'chart' },
  { k: 'barres', label: 'Barres', icone: 'sigma' },
  { k: 'aires', label: 'Aires', icone: 'wave' },
];

const suffixe = (u: string | null) => (u === 'pourcent' ? ' %' : u === 'jours' ? ' j' : '');

/**
 * Le graphe. Un seul composant pour les trois formes : ce qui change est le
 * TRACÉ, pas les axes ni l'échelle — deux implémentations auraient fini par
 * cadrer différemment le même jeu de points.
 *
 * ⚠️ Les segments sont tracés PAR MORCEAUX CONTINUS. Un point non calculable
 * coupe la ligne au lieu de la faire sauter par-dessus.
 */
/* ⚠️ EXPORTÉ depuis le 08/10/2026 : le tableau de bord de la qualité analytique
   (`QualiteAnalytique.tsx`) RÉEMPLOIE ce socle au lieu de le dupliquer. */
export function Graphe({ points, cle, def, forme }: {
  points: Point[]; cle: string; def: Def | null; forme: Forme;
}) {
  const W = 900; const H = 260; const M = { g: 52, d: 14, h: 16, b: 46 };
  const utiles = points.map((p) => p.valeurs?.[cle]).map((v) => (v && v.calculable && v.valeur !== null ? v.valeur : null));
  const chiffres = utiles.filter((v): v is number => v !== null);
  if (!chiffres.length) {
    return (
      <EmptyState titre="Aucune valeur calculable sur cette période"
        indice="L’indicateur existe, mais le registre ne porte rien à compter sur la fenêtre choisie — ce n’est pas un zéro." />
    );
  }
  const pourcent = def?.unite === 'pourcent';
  const maxBrut = Math.max(...chiffres); const minBrut = Math.min(...chiffres);
  /* Un pourcentage se lit sur 0–100 : le cadrer sur ses valeurs ferait passer
     un écart de deux points pour un effondrement. */
  const haut = pourcent ? 100 : Math.max(maxBrut, 1) * 1.1;
  const bas = pourcent ? 0 : Math.min(0, minBrut);
  const etendue = haut - bas || 1;

  const larg = W - M.g - M.d;
  const x = (i: number) => M.g + (points.length === 1 ? larg / 2 : (i / (points.length - 1)) * larg);
  const y = (v: number) => M.h + (1 - (v - bas) / etendue) * (H - M.h - M.b);
  const paliers = [0, 0.25, 0.5, 0.75, 1].map((f) => bas + f * etendue);

  /* Les morceaux continus : chaque suite de points calculables d'affilée. */
  const morceaux: { i: number; v: number }[][] = [];
  let courant: { i: number; v: number }[] = [];
  utiles.forEach((v, i) => {
    if (v === null) { if (courant.length) morceaux.push(courant); courant = []; return; }
    courant.push({ i, v });
  });
  if (courant.length) morceaux.push(courant);

  const pasEtiquette = Math.ceil(points.length / 12);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: 'block' }}
      aria-label={`${def?.libelle || cle} sur ${points.length} période(s), de ${chiffres[0]} à ${chiffres[chiffres.length - 1]}`}>
      {paliers.map((p, i) => (
        <g key={i}>
          <line x1={M.g} x2={W - M.d} y1={y(p)} y2={y(p)} stroke="var(--border-subtle)" strokeWidth={1} />
          <text x={M.g - 8} y={y(p) + 4} textAnchor="end" fontSize={11} fill="var(--text-faint)">
            {pourcent ? `${Math.round(p)} %` : Math.round(p * 10) / 10}
          </text>
        </g>
      ))}

      {/* Les périodes entamées, hachurées : elles ne se comparent pas aux autres. */}
      <defs>
        <pattern id="kpi-tronque" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--amber-600)" strokeWidth="1.4" opacity="0.5" />
        </pattern>
      </defs>
      {points.map((p, i) => (p.tronquee ? (
        <rect key={`t${i}`} x={x(i) - larg / Math.max(points.length, 2) / 2} y={M.h}
          width={larg / Math.max(points.length, 2)} height={H - M.h - M.b} fill="url(#kpi-tronque)" />
      ) : null))}

      {forme === 'barres' ? (
        points.map((p, i) => {
          const v = utiles[i];
          if (v === null) return null;
          const l = Math.max(6, (larg / Math.max(points.length, 1)) * 0.55);
          return <rect key={i} x={x(i) - l / 2} y={y(v)} width={l} height={Math.max(1, y(bas) - y(v))}
            fill="var(--color-primary)" rx={2} />;
        })
      ) : (
        morceaux.map((mo, k) => {
          const d = mo.map((p, j) => `${j ? 'L' : 'M'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
          return (
            <g key={k}>
              {forme === 'aires' && mo.length > 1 && (
                <path d={`${d} L${x(mo[mo.length - 1].i).toFixed(1)},${y(bas).toFixed(1)} L${x(mo[0].i).toFixed(1)},${y(bas).toFixed(1)} Z`}
                  fill="var(--cyan-100)" opacity={0.75} />
              )}
              <path d={d} fill="none" stroke="var(--color-primary)" strokeWidth={2.2} strokeLinejoin="round" />
            </g>
          );
        })
      )}

      {points.map((p, i) => {
        const v = utiles[i];
        if (v === null) return null;
        return (
          <circle key={`p${i}`} cx={x(i)} cy={y(v)} r={3.4} fill="var(--color-primary-active)">
            <title>{`${p.libelle} — ${p.valeurs[cle]?.texte}${p.tronquee ? ' (période entamée)' : ''}`}</title>
          </circle>
        );
      })}

      {points.map((p, i) => (i % pasEtiquette === 0 ? (
        /* ⚠️ LES ÉTIQUETTES DES BORDS NE SE CENTRENT PAS (relevé SYS-13 :
           « axe X coupé — août 2 »). Centrée sur le premier point, une étiquette
           déborde à gauche du cadre et se fait rogner en plein milieu d'un mot.
           On l'ancre au début à gauche, à la fin à droite : elle reste sous son
           point sans sortir du dessin. */
        <text key={`l${i}`} x={x(i)} y={H - 24}
          textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
          fontSize={11} fill="var(--text-muted)">
          {p.libelle.replace(/ 20(\d\d)$/, ' $1')}
        </text>
      ) : null))}
    </svg>
  );
}

export function Kpi() {
  const session = useGed((s) => s.session);
  const [serie, setSerie] = useState<Serie | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [cle, setCle] = useState('');
  const [granularite, setGranularite] = useState<Granularite>('mois');
  /* ⚠️ « courbe » PAR DÉFAUT, mais pas quand il n'y a rien à relier (SYS-13 :
     « courbe sur 13 mois avec 1 point tracé »). Une ligne dit une TENDANCE :
     tracée entre moins de trois mesures, elle en affirme une qui n'existe pas —
     et sur un seul point, elle ne dessine littéralement rien, ce qui laisse un
     cadre vide alors qu'une mesure existe.
     Le choix reste à l'utilisateur : on ne fige pas la forme, on choisit
     seulement celle qui ne ment pas au premier affichage. */
  /* ⚠️ LA FORME PAR DÉFAUT EST DÉRIVÉE, plus stockée (02/09/2026).
     C'était un état recalculé par un effet à chaque changement de série : le
     graphe se peignait une fois en courbe, puis une fois en barres. Sur deux ou
     trois points mesurés — précisément le cas que cette règle vise — le
     clignotement portait sur toute la surface du graphe.
     Or ce n'est PAS un état : c'est une lecture de la série. Tant que personne
     n'a choisi, la forme se déduit ; dès qu'on choisit, le choix prime. Un seul
     état suffit donc, et il porte le CHOIX, pas son résultat. */
  const [formeChoix, setFormeChoix] = useState<Forme | null>(null);
  const mesurees = (serie?.points || [])
    .filter((p) => p.valeurs?.[cle]?.calculable && p.valeurs[cle].valeur !== null).length;
  const forme: Forme = formeChoix ?? (mesurees < 3 ? 'barres' : 'courbe');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  /* B2 — la période OUVERTE : cliquer une ligne du tableau charge la liste des
     événements que le chiffre compte. `null` = rien d'ouvert. */
  const [ouvert, setOuvert] = useState<Point | null>(null);
  const [evts, setEvts] = useState<Ctx | null>(null);
  const [evtsChargement, setEvtsChargement] = useState(false);

  const charger = useCallback(async (g: Granularite, d: string, a: string) => {
    setChargement(true); setErreur('');
    const p = new URLSearchParams({ granularite: g });
    if (d) p.set('du', d);
    if (a) p.set('au', a);
    try {
      const r = await fetch(`/api/ged/ged/kpi/serie?${p}`, { credentials: 'include' });
      if (!r.ok) { setErreur('La série n’a pas pu être lue.'); return; }
      const j = (await r.json()) as Serie;
      setSerie(j); setDu(j.du); setAu(j.au);
      setCle((c) => (c && j.catalogue.some((x) => x.cle === c) ? c : (j.catalogue[0]?.cle || '')));
    } catch {
      setErreur('La série n’a pas pu être lue.');
    } finally {
      setChargement(false);
    }
  }, []);

  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     `charger` est un `useCallback` ASYNC : son `setState` se produit APRÈS un
     `await`, donc dans une continuation, jamais dans le corps de l'effet. La
     règle ne franchit pas la frontière asynchrone et l'annonce quand même.
     C'est l'usage canonique d'un effet — « s'abonner à un système externe ».
     ⚠️ Le silence est CIBLÉ, ligne à ligne : désactiver la règle pour le
     fichier masquerait un vrai cas ajouté demain. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void charger(granularite, '', ''); }, [charger, granularite]);
  /* Changer d'indicateur ferme le détail : garder ouverte la liste d'un AUTRE
     indicateur sous le nouveau graphe ferait lire les mauvais événements. */
  /* Ajusté au rendu : garder ouverte, ne serait-ce qu'une passe, la liste
     d'événements d'un AUTRE indicateur ferait lire les mauvais faits sous le
     nouveau graphe. */
  const [clePrec, setClePrec] = useState(cle);
  if (cle !== clePrec) { setClePrec(cle); setOuvert(null); setEvts(null); }

  const ouvrirPeriode = useCallback(async (p: Point) => {
    if (ouvert?.cle === p.cle) { setOuvert(null); setEvts(null); return; }
    setOuvert(p); setEvts(null); setEvtsChargement(true);
    try {
      const r = await fetch(`/api/ged/ged/kpi/evenements?cle=${encodeURIComponent(cle)}&du=${p.debut}&au=${p.fin}`, { credentials: 'include' });
      setEvts(r.ok ? await r.json() : { motif: 'La liste n’a pas pu être lue.' });
    } catch {
      setEvts({ motif: 'La liste n’a pas pu être lue.' });
    } finally {
      setEvtsChargement(false);
    }
  }, [cle, ouvert]);

  const def = useMemo(() => serie?.catalogue.find((d) => d.cle === cle) || null, [serie, cle]);
  /* Mémoïsé : `dernier` en dépend, et `serie?.points || []` fabriquait un
     tableau neuf à chaque rendu quand la série est absente — le compilateur
     cessait alors de mémoïser toute la fonction. */
  const points = useMemo(() => serie?.points || [], [serie]);
  /* Les périodes sans mesure, et le motif qu'elles partagent — s'il est le même
     pour toutes. Deux motifs différents, et l'on retombe sur l'affichage ligne
     par ligne : c'est alors une vraie information par ligne. */
  const nonCalculables = points.filter((p) => p.valeurs?.[cle] && !p.valeurs[cle].calculable);
  const nbNonCalculables = nonCalculables.length;
  const motifsDistincts = new Set(nonCalculables.map((p) => p.valeurs[cle].motif));
  const motifCommun = nbNonCalculables > 1 && motifsDistincts.size === 1
    ? [...motifsDistincts][0] : '';
  /* ⚠️ PLUS DE `useMemo` ICI (02/09/2026). C'est un parcours de tableau sur
     quelques dizaines de points : la mémoïsation manuelle ne gagnait rien et
     EMPÊCHAIT le compilateur React de mémoïser le composant entier
     (« Compilation Skipped: existing memoization could not be preserved »).
     Un `useMemo` écrit à la main qui bloque l'optimisation automatique coûte
     plus qu'il ne rapporte. */
  const dernier = (() => {
    for (let i = points.length - 1; i >= 0; i -= 1) {
      const v = points[i]?.valeurs?.[cle];
      if (v?.calculable && v.valeur !== null) return { p: points[i], v };
    }
    return null;
  })();
  /* La comparaison se fait au point calculable PRÉCÉDENT, pas au premier de la
     série : « depuis le mois dernier » et « depuis un an » ne disent pas la
     même chose, et c'est le premier qu'on lit sur un graphe. */
  const avant = useMemo(() => {
    let vu = false;
    for (let i = points.length - 1; i >= 0; i -= 1) {
      const v = points[i]?.valeurs?.[cle];
      if (!v?.calculable || v.valeur === null) continue;
      if (!vu) { vu = true; continue; }
      return v;
    }
    return null;
  }, [points, cle]);
  /* ⚠️ AUCUN ÉCART SUR UNE PÉRIODE ENTAMÉE. Août à 1/1 contre juillet à
     650/915 donnait « ▲ 29 % depuis la période précédente » : arithmétiquement
     juste, éditorialement faux. Un écart calculé sur un mois pas fini est
     précisément la tendance imaginaire que tout le reste de cet écran
     s'emploie à ne pas fabriquer. */
  const ecart = dernier && !dernier.p.tronquee && avant && avant.valeur !== null && dernier.v.valeur !== null
    ? Math.round((dernier.v.valeur - avant.valeur) * 10) / 10 : null;

  if (!session.roles.length) return null;

  const champ: React.CSSProperties = { ...inputStyle, height: 34, fontSize: 12.5, width: 'auto', minWidth: 120 };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Pilotage · <RefISO code="8.6" /></>} title="Indicateurs — suivi de l’activité" />

      {/* La barre de réglages, AU-DESSUS du graphe : ce qu'on change en premier
          se place là où l'œil arrive. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 260 }}>
          <label htmlFor="kpi-ind" style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 4 }}>
            Indicateur
          </label>
          <select id="kpi-ind" value={cle} onChange={(e) => setCle(e.target.value)}
            style={{ ...inputStyle, height: 36, fontSize: 13, width: '100%' }}>
            {(serie?.catalogue || []).map((d) => <option key={d.cle} value={d.cle}>{d.libelle}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="kpi-gr" style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 4 }}>
            Granularité
          </label>
          <select id="kpi-gr" value={granularite} onChange={(e) => setGranularite(e.target.value as Granularite)} style={champ}>
            {GRANULARITES.map((g) => <option key={g.k} value={g.k}>{g.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Segment ariaLabel="Forme d’affichage" valeur={forme} onChange={setFormeChoix} options={FORMES.map((f) => ({ valeur: f.k, libelle: <><I n={f.icone} s={13} />{f.label}</>, aide: `Afficher en ${f.label.toLowerCase()}` }))} />
        </div>
        {/* `flexWrap` : sur un téléphone, deux dates et « Actualiser » ne
            tiennent pas sur une ligne (la page débordait, 19/10/2026). */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <label htmlFor="kpi-du" style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 4 }}>Du</label>
            <input id="kpi-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} style={champ} />
          </div>
          <div>
            <label htmlFor="kpi-au" style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 4 }}>Au</label>
            <input id="kpi-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} style={champ} />
          </div>
          <Bouton taille="sm" type="button" onClick={() => void charger(granularite, du, au)}>
            <I n="refresh" s={13} /> Actualiser
          </Bouton>
        </div>
      </div>

      {erreur && (
        <div style={{ ...card, padding: 14, background: 'var(--red-50)', border: '1px solid var(--red-500)', fontSize: 12.5 }}>{erreur}</div>
      )}

      {chargement ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={4} height={60} /></div>
      ) : !serie || !points.length ? (
        <EmptyState titre="Aucune période à tracer"
          indice="Élargissez la fenêtre de dates, ou changez la granularité." />
      ) : (
        <>
          {/* LE GRAPHE D'ABORD — c'est la demande, et c'est ce qui se lit. */}
          <div style={{ ...card, padding: '14px 16px 6px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>{def?.libelle || cle}</h2>
              {dernier && (
                <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-primary-active)', fontFamily: 'var(--font-mono)' }}>
                  {dernier.v.valeur}{suffixe(def?.unite ?? null)}
                </span>
              )}
              {ecart !== null && ecart !== 0 && (
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>
                  {ecart > 0 ? '▲' : '▼'} {Math.abs(ecart)}{suffixe(def?.unite ?? null)} depuis la période précédente
                </span>
              )}
              {dernier?.p.tronquee && (
                <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>
                  dernière période entamée — ni comparable, ni comparée
                </span>
              )}
            </div>
            {def?.aide && (
              <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '90ch' }}>{def.aide}</p>
            )}
            <Graphe points={points} cle={cle} def={def} forme={forme} />
          </div>

          {/* ⚠️ Le motif COMMUN à toutes les périodes non calculables. Quand
              douze lignes portent la même phrase, ce n'est pas douze fois une
              information : c'est une seule, et elle se dit une fois. */}
          {serie.avertissement && (
            <div style={{ ...card, padding: 12, background: 'var(--amber-50)', fontSize: 12.5, lineHeight: 1.55 }}>
              {serie.avertissement}
            </div>
          )}

          {/* Le détail, SOUS le graphe : on vient voir une tendance, on vérifie
              ensuite ce qu'elle compte. C'est aussi la réponse à B2 — le
              numérateur et le dénominateur de chaque point sont lisibles. */}
          {motifCommun && (
            <div style={{ ...card, padding: '11px 14px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
              <strong>{nbNonCalculables} période(s) sur {points.length} sans mesure.</strong> {motifCommun}
            </div>
          )}
          {/* L7 vague 2 (11/10/2026) : registre SIMPLE sur le socle — la période
              la plus récente en tête (ordre métier), la ligne ouvre la liste des
              événements comptés. Les états restent AUTOUR : le graphe et
              l'avertissement de série en dépendent aussi. */}
          <Tableau
            id="kpi-releves"
            ajusterLargeur
            niveau="simple"
            lignes={[...points].reverse()}
            cleLigne={(p) => p.cle}
            ligneProps={(p) => ligneOuvrable(() => void ouvrirPeriode(p), `Ouvrir la liste des événements comptés sur ${p.libelle}`,
              { background: ouvert?.cle === p.cle ? 'var(--cyan-50)' : undefined })}
            colonnes={[
              {
                cle: 'periode', libelle: 'Période', type: 'date', valeur: (p) => p.debut,
                rendu: (p) => (
                  <>
                    {p.libelle}
                    <span style={{ color: 'var(--text-faint)', fontSize: 12.5, marginInlineStart: 8 }}>{fmtDate(p.debut)} → {fmtDate(p.fin)}</span>
                  </>
                ),
              },
              {
                cle: 'valeur', libelle: 'Valeur', type: 'nombre', style: { fontWeight: 600 },
                valeur: (p) => { const v = p.valeurs?.[cle]; return v?.calculable && v.valeur !== null ? v.valeur : null; },
                rendu: (p) => { const v = p.valeurs?.[cle]; return v?.calculable && v.valeur !== null ? `${v.valeur}${suffixe(def?.unite ?? null)}` : '—'; },
              },
              {
                cle: 'compte', libelle: 'Compté sur', type: 'nombre', style: { color: 'var(--text-muted)' },
                valeur: (p) => p.valeurs?.[cle]?.denominateur ?? null,
                rendu: (p) => {
                  const v = p.valeurs?.[cle];
                  return v?.numerateur !== null && v?.denominateur !== null && v?.denominateur !== undefined ? `${v.numerateur} / ${v.denominateur}` : '—';
                },
              },
              {
                cle: 'remarque', libelle: 'Remarque', style: { color: 'var(--text-muted)' },
                valeur: (p) => { const v = p.valeurs?.[cle]; return `${p.tronquee ? 'période entamée ' : ''}${v && !v.calculable ? (v.motif === motifCommun ? 'rien à mesurer' : v.motif) : ''}`.trim(); },
                rendu: (p) => {
                  const v = p.valeurs?.[cle];
                  return (
                    <>
                      {p.tronquee && <span style={{ color: 'var(--amber-600)', fontWeight: 600, marginInlineEnd: 8 }}>période entamée</span>}
                      {/* ⚠️ LE MOTIF NE SE RÉPÈTE PLUS (relevé SYS-13). La même
                          phrase — « Aucun cas sur la période : un taux calculé sur
                          zéro n'a pas de sens » — s'écrivait sur les douze lignes.
                          Répétée, une explication cesse d'être lue : elle devient
                          une texture. On garde le mot court dans la ligne, et la
                          phrase complète UNE fois sous le tableau, là où elle
                          s'adresse à toute la série.
                          ⚠️ Le motif reste montré tel quel s'il est PARTICULIER à
                          la ligne : c'est la répétition qu'on supprime, jamais
                          l'explication. */}
                      {v && !v.calculable ? (v.motif === motifCommun ? 'rien à mesurer' : v.motif) : ''}
                    </>
                  );
                },
              },
            ]}
          />

          {/* B2 — CE QUE LE CHIFFRE COMPTE. La liste vient de la même période
              que le point, et la route la confronte au chiffre : si les deux
              divergent, l'avertissement s'affiche au lieu de se taire. */}
          {ouvert && (
            <div style={{ ...card, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>
                  Ce que compte « {def?.libelle || cle} » — {ouvert.libelle}
                </h3>
                <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>{ouvert.debut} → {ouvert.fin}</span>
                <Bouton taille="sm" type="button" onClick={() => { setOuvert(null); setEvts(null); }} style={{ marginInlineStart: 'auto' }}>
                  Fermer
                </Bouton>
              </div>
              {evtsChargement ? (
                <Skeleton rows={3} height={30} />
              ) : !evts ? null : evts.coherent === false ? (
                <div style={{ padding: 12, borderRadius: 'var(--radius-sm)', background: 'var(--red-50)', border: '1px solid var(--red-500)', fontSize: 12.5, lineHeight: 1.55 }}>
                  {evts.motifIncoherence}
                </div>
              ) : !evts.evenements?.length ? (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  {evts.motif || 'Aucun événement sur cette période — le chiffre ne compte rien, il ne cache rien.'}
                </p>
              ) : (
                <>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
                    {evts.evenements.map((e: Ctx, i: number) => (
                      <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12.5, padding: '5px 8px', borderRadius: 'var(--radius-sm)', background: i % 2 ? 'var(--gray-25)' : undefined }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', flex: '0 0 auto' }}>{e.ref}</span>
                        <span style={{ flex: 1, minWidth: 0 }}>{e.libelle}</span>
                        <span style={{ fontSize: 12.5, color: e.auNumerateur ? 'var(--color-primary-active)' : 'var(--text-faint)', fontWeight: e.auNumerateur ? 600 : 400, flex: '0 0 auto' }}>
                          {e.etat}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {evts.tronques > 0 && (
                    <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--amber-600)' }}>
                      {evts.tronques} événement(s) non affichés — la liste est plafonnée, les comptés d’abord.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '90ch' }}>
            {serie.rappel}
          </p>
        </>
      )}
    </div>
  );
}
